import { useEffect, useMemo, useState } from 'react';
import { act } from '../lib/actions';
import { api } from '../lib/api';
import { useNova } from '../lib/store';
import type { InteractionRow } from '../lib/types';
import { Field, inputClass, Pill } from './admin/kit';
import { CHANNELS } from './ContextPanel';
import { TranscriptBody } from './Conversations';
import { RecordingPlayer } from './RecordingPlayer';
import { Button, duration, Panel } from './ui';

// ------------------------------------------------------------------ types and scoring (as the API scores)
export interface QaQuestion {
  id: string;
  text: string;
  type: 'yes_no' | 'rating';
  points: number;
  critical?: boolean;
  allowNa?: boolean;
  help?: string;
}
export interface QaDefinition {
  sections: { id: string; title: string; questions: QaQuestion[] }[];
}
export interface Scorecard {
  id: string;
  family_id: string;
  version: number;
  name: string;
  channel: 'all' | 'voice' | 'chat' | 'email';
  pass_mark: number;
  definition: QaDefinition;
  active: boolean;
  reviews: number;
}
type Value = 'yes' | 'no' | 'na' | number | null;
type Answers = Record<string, { value: Value; comment?: string }>;
interface Review {
  id: string;
  status: 'draft' | 'submitted' | 'acknowledged' | 'disputed';
  scorecard_id: string;
  scorecard_name: string;
  version: number;
  definition: QaDefinition;
  pass_mark: number;
  interaction_id: string;
  channel: string;
  direction: string;
  started_at: string;
  talk_seconds: number | null;
  subject: string | null;
  customer_number: string | null;
  customer: string | null;
  agent_id: string;
  agent_name: string | null;
  reviewer_id: string;
  reviewer_name: string | null;
  queue_name: string | null;
  recording_id: string | null;
  answers: Answers;
  comment: string | null;
  marks: { at: number; text: string }[];
  score: number | null;
  passed: boolean | null;
  critical_failed: boolean;
  dispute_reason: string | null;
  dispute_reply: string | null;
  submitted_at: string | null;
}

const fits = (card: string, channel: string) => card === 'all' || card === channel || (card === 'chat' && ['chat', 'whatsapp', 'telegram'].includes(channel));

/** The score as the API computes it: points earned out of points possible, N/A left out; a failed critical question fails the review. */
export function liveScore(d: QaDefinition, answers: Answers, passMark: number) {
  let got = 0;
  let possible = 0;
  let critical = false;
  let open = 0;
  for (const q of d.sections.flatMap((s) => s.questions)) {
    const v = answers[q.id]?.value ?? null;
    if (v === null) {
      open++;
      continue;
    }
    if (v === 'na') continue;
    possible += q.points;
    got += q.type === 'yes_no' ? (v === 'yes' ? q.points : 0) : typeof v === 'number' ? (q.points * (v - 1)) / 4 : 0;
    if (q.critical && (v === 'no' || (typeof v === 'number' && v <= 2))) critical = true;
  }
  const score = possible ? Math.round((got / possible) * 1000) / 10 : null;
  return { score, critical, open, passed: score === null ? null : !critical && score >= passMark };
}

const STATUS: Record<Review['status'], { label: string; tone: 'line' | 'good' | 'bad' | 'warn' }> = {
  draft: { label: 'Draft', tone: 'line' },
  submitted: { label: 'Waiting for the agent', tone: 'warn' },
  acknowledged: { label: 'Acknowledged', tone: 'good' },
  disputed: { label: 'Disputed', tone: 'bad' },
};

function Result({ score, passed, critical }: { score: number | null; passed: boolean | null; critical: boolean }) {
  if (score === null) return <span className="text-ink-3">–</span>;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="tabular font-semibold">{score}%</span>
      <Pill tone={passed ? 'good' : 'bad'}>{critical ? 'Fail (critical)' : passed ? 'Pass' : 'Fail'}</Pill>
    </span>
  );
}

const when = (s: string) => new Date(s).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
const day = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// ------------------------------------------------------------------ to review
interface Candidate {
  id: string;
  channel: string;
  direction: string;
  started_at: string;
  talk_seconds: number | null;
  subject: string | null;
  customer_number: string | null;
  customer_name: string | null;
  customer_email: string | null;
  agent_name: string;
  agent_id: string;
  queue_name: string | null;
  outcome?: string | null;
  has_recording: boolean;
  my_draft?: string | null;
}

/**
 * What to review: finished conversations nobody has reviewed, newest first,
 * or a fair random sample per agent. Opening one starts a review with a
 * scorecard for its channel.
 */
export function QaToReview({ onOpen }: { onOpen: (reviewId: string) => void }) {
  const [rows, setRows] = useState<Candidate[] | null>(null);
  const [channel, setChannel] = useState('');
  const [sample, setSample] = useState({ perAgent: '3', from: day(new Date(Date.now() - 7 * 86_400_000)), to: day(new Date()) });
  const [cards, setCards] = useState<Scorecard[]>([]);
  const [picking, setPicking] = useState<Candidate | null>(null);
  const load = () => {
    const qs = new URLSearchParams({ limit: '100' });
    if (channel) qs.set('channel', channel);
    api<Candidate[]>('GET', `/qa/to-review?${qs}`).then(setRows, () => setRows([]));
  };
  useEffect(load, [channel]);
  useEffect(() => {
    api<Scorecard[]>('GET', '/qa/scorecards').then(setCards, () => setCards([]));
  }, []);
  async function draw() {
    const r = await act(() => api<Candidate[]>('POST', '/qa/sample', { perAgent: Number(sample.perAgent), from: sample.from, to: sample.to, ...(channel ? { channel } : {}) }));
    if (r) setRows(r);
  }
  async function start(c: Candidate, card: Scorecard) {
    const r = await act(() => api<Review>('POST', '/qa/reviews', { interactionId: c.id, scorecardId: card.id }));
    if (r) onOpen(r.id);
  }
  function open(c: Candidate) {
    if (c.my_draft) return onOpen(c.my_draft);
    const usable = cards.filter((s) => fits(s.channel, c.channel));
    if (usable.length === 1) return void start(c, usable[0]);
    setPicking(c);
  }
  return (
    <Panel className="p-5">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-medium">To review</h1>
          <p className="text-sm text-ink-3">Finished conversations nobody has reviewed yet. You cannot review your own.</p>
        </div>
        <select value={channel} onChange={(e) => setChannel(e.target.value)} aria-label="Channel" className="rounded-xl border border-line bg-abyss/60 px-3 py-2 text-sm">
          <option value="">All channels</option>
          <option value="voice">Calls</option>
          <option value="chat">Chats (livechat, WhatsApp, Telegram)</option>
          <option value="email">Emails</option>
        </select>
      </div>
      <div className="mb-4 flex flex-wrap items-end gap-2 rounded-lg border border-line p-3" aria-label="Random sample">
        <span className="w-full text-[12px] text-ink-3">A fair sample: a few random conversations for each agent, so reviews are not hand-picked.</span>
        <label className="text-[12px] text-ink-2">
          Per agent
          <input type="number" min={1} max={20} value={sample.perAgent} onChange={(e) => setSample({ ...sample, perAgent: e.target.value })} className={`${inputClass} mt-1 w-20`} />
        </label>
        <label className="text-[12px] text-ink-2">
          From
          <input type="date" value={sample.from} onChange={(e) => setSample({ ...sample, from: e.target.value })} className={`${inputClass} mt-1`} />
        </label>
        <label className="text-[12px] text-ink-2">
          To
          <input type="date" value={sample.to} onChange={(e) => setSample({ ...sample, to: e.target.value })} className={`${inputClass} mt-1`} />
        </label>
        <Button tone="quiet" className="px-3 py-2 text-sm" onClick={() => void draw()}>
          Draw a sample
        </Button>
        <Button tone="ghost" className="px-3 py-2 text-sm" onClick={load}>
          Show all
        </Button>
      </div>
      {!cards.length ? <p className="mb-3 text-sm text-break">There are no scorecards yet. An admin adds them under Admin &gt; Quality &gt; Scorecards.</p> : null}
      {rows === null ? (
        <p className="py-8 text-center text-sm text-ink-3">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="py-8 text-center text-sm text-ink-2">Nothing to review here.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-sm" aria-label="Conversations to review">
            <thead className="text-ink-3">
              <tr className="border-b border-line">
                <th className="py-2 pr-3 font-normal">When</th>
                <th className="py-2 pr-3 font-normal">Channel</th>
                <th className="py-2 pr-3 font-normal">Agent</th>
                <th className="py-2 pr-3 font-normal">Customer</th>
                <th className="py-2 pr-3 font-normal">Queue</th>
                <th className="py-2 pr-3 text-right font-normal">Length</th>
                <th className="py-2 font-normal" aria-label="Review" />
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className="border-b border-line/60">
                  <td className="py-2.5 pr-3 text-ink-2">{when(c.started_at)}</td>
                  <td className="py-2.5 pr-3">{CHANNELS[c.channel]?.label ?? c.channel}</td>
                  <td className="py-2.5 pr-3">{c.agent_name}</td>
                  <td className="py-2.5 pr-3 text-ink-2">{c.customer_name ?? c.customer_email ?? c.customer_number ?? ''}</td>
                  <td className="py-2.5 pr-3 text-ink-2">{c.queue_name ?? ''}</td>
                  <td className="tabular py-2.5 pr-3 text-right">{c.talk_seconds ? duration(c.talk_seconds) : ''}</td>
                  <td className="py-2.5 text-right">
                    <Button tone="quiet" className="px-2.5 py-1 text-xs" disabled={!cards.some((s) => fits(s.channel, c.channel))} onClick={() => open(c)}>
                      {c.my_draft ? 'Continue' : 'Review'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {picking ? (
        <div className="fixed inset-0 z-50 grid place-items-center bg-abyss/60" onMouseDown={() => setPicking(null)}>
          <div role="dialog" aria-label="Choose a scorecard" className="w-[min(420px,90vw)] rounded-xl border border-line-strong bg-hull p-4 shadow-2xl" onMouseDown={(e) => e.stopPropagation()}>
            <p className="mb-3 font-medium">Choose a scorecard</p>
            <ul className="flex flex-col gap-2">
              {cards
                .filter((s) => fits(s.channel, picking.channel))
                .map((s) => (
                  <li key={s.id}>
                    <Button tone="quiet" className="w-full justify-start px-3 py-2 text-sm" onClick={() => void start(picking, s)}>
                      {s.name}
                    </Button>
                  </li>
                ))}
            </ul>
          </div>
        </div>
      ) : null}
    </Panel>
  );
}

// ------------------------------------------------------------------ the list of reviews
export function QaReviews({ onOpen, mine = false }: { onOpen: (id: string) => void; mine?: boolean }) {
  const role = useNova((s) => s.user?.role);
  const agent = role === 'agent';
  const [rows, setRows] = useState<(Review & { customer: string | null })[] | null>(null);
  const [status, setStatus] = useState('');
  useEffect(() => {
    const qs = new URLSearchParams();
    if (status) qs.set('status', status);
    if (mine && !agent) qs.set('mine', '1');
    api<(Review & { customer: string | null })[]>('GET', `/qa/reviews?${qs}`).then(setRows, () => setRows([]));
  }, [status, mine, agent]);
  return (
    <Panel className="p-5">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-medium">{agent ? 'My reviews' : mine ? 'My reviews as reviewer' : 'Reviews'}</h1>
          <p className="text-sm text-ink-3">{agent ? 'How your conversations were scored. Open one to see why, then acknowledge it or dispute it.' : 'Submitted reviews, and your own drafts.'}</p>
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status" className="rounded-xl border border-line bg-abyss/60 px-3 py-2 text-sm">
          <option value="">Every status</option>
          {!agent ? <option value="draft">Drafts</option> : null}
          <option value="submitted">Waiting for the agent</option>
          <option value="disputed">Disputed</option>
          <option value="acknowledged">Acknowledged</option>
        </select>
      </div>
      {rows === null ? (
        <p className="py-8 text-center text-sm text-ink-3">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="py-8 text-center text-sm text-ink-2">No reviews yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-sm" aria-label="Reviews">
            <thead className="text-ink-3">
              <tr className="border-b border-line">
                <th className="py-2 pr-3 font-normal">Reviewed</th>
                {!agent ? <th className="py-2 pr-3 font-normal">Agent</th> : null}
                <th className="py-2 pr-3 font-normal">Conversation</th>
                <th className="py-2 pr-3 font-normal">Scorecard</th>
                <th className="py-2 pr-3 font-normal">Score</th>
                <th className="py-2 pr-3 font-normal">Status</th>
                <th className="py-2 font-normal" aria-label="Open" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-line/60">
                  <td className="py-2.5 pr-3 text-ink-2">{r.submitted_at ? when(r.submitted_at) : 'Not yet'}</td>
                  {!agent ? <td className="py-2.5 pr-3">{r.agent_name}</td> : null}
                  <td className="py-2.5 pr-3 text-ink-2">
                    {CHANNELS[r.channel]?.label ?? r.channel} · {r.customer ?? ''} · {when(r.started_at)}
                  </td>
                  <td className="py-2.5 pr-3">{r.scorecard_name}</td>
                  <td className="py-2.5 pr-3">
                    <Result score={r.score} passed={r.passed} critical={r.critical_failed} />
                  </td>
                  <td className="py-2.5 pr-3">
                    <Pill tone={STATUS[r.status].tone}>{STATUS[r.status].label}</Pill>
                  </td>
                  <td className="py-2.5 text-right">
                    <Button tone="ghost" className="px-2.5 py-1 text-xs" onClick={() => onOpen(r.id)}>
                      Open
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

// ------------------------------------------------------------------ one review
const parseAt = (s: string): number | null => {
  const m = /^(\d{1,3}):([0-5]\d)$/.exec(s.trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

/**
 * One review: the conversation on the left (recording or transcript), the
 * scorecard on the right. The reviewer fills it in (live score) and submits;
 * the agent reads it and acknowledges or disputes; the reviewer answers a
 * dispute.
 */
export function QaReviewView({ id, onBack }: { id: string; onBack: () => void }) {
  const me = useNova((s) => s.user);
  const [r, setR] = useState<Review | null>(null);
  const [answers, setAnswers] = useState<Answers>({});
  const [comment, setComment] = useState('');
  const [marks, setMarks] = useState<{ at: number; text: string }[]>([]);
  const [markAt, setMarkAt] = useState('');
  const [markText, setMarkText] = useState('');
  const [reason, setReason] = useState('');
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const load = (x?: Review) => {
    const apply = (v: Review) => {
      setR(v);
      setAnswers(v.answers ?? {});
      setComment(v.comment ?? '');
      setMarks(v.marks ?? []);
    };
    if (x) apply(x);
    else api<Review>('GET', `/qa/reviews/${id}`).then(apply, () => undefined);
  };
  useEffect(() => load(), [id]);
  const live = useMemo(() => (r ? liveScore(r.definition, answers, r.pass_mark) : null), [r, answers]);
  if (!r || !me) return <Panel className="p-5 text-sm text-ink-3">Loading the review…</Panel>;

  const reviewer = r.reviewer_id === me.sub || ['supervisor', 'admin', 'manager', 'platform_admin'].includes(me.role);
  const editing = reviewer && (r.status === 'draft' || r.status === 'disputed');
  const agentView = r.agent_id === me.sub;
  const set = (qid: string, value: Value) => setAnswers((a) => ({ ...a, [qid]: { ...a[qid], value } }));
  const note = (qid: string, text: string) => setAnswers((a) => ({ ...a, [qid]: { value: a[qid]?.value ?? null, comment: text } }));

  async function save(): Promise<boolean> {
    const x = await act(() => api<Review>('PATCH', `/qa/reviews/${id}`, { answers, comment: comment || null, marks }));
    if (x) load(x);
    return Boolean(x);
  }
  async function run(path: string, body: unknown, msg: string) {
    setBusy(true);
    const x = await act(() => api<Review>('POST', `/qa/reviews/${id}/${path}`, body), msg);
    setBusy(false);
    if (x) load(x);
  }
  const row = { id: r.interaction_id, channel: r.channel } as InteractionRow;

  return (
    <div className="mx-auto flex max-w-[1400px] flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <Button tone="ghost" className="px-2.5 py-1.5 text-sm" onClick={onBack}>
          ← Back
        </Button>
        <h1 className="text-lg font-medium">
          Review of {r.agent_name} · {CHANNELS[r.channel]?.label ?? r.channel} with {r.customer ?? r.customer_number ?? 'a customer'}
        </h1>
        <Pill tone={STATUS[r.status].tone}>{STATUS[r.status].label}</Pill>
      </div>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_480px]">
        <Panel className="min-w-0 p-4">
          <p className="mb-3 text-[12px] text-ink-3">
            {when(r.started_at)} · {r.queue_name ?? 'No queue'} · {r.talk_seconds ? duration(r.talk_seconds) : ''} {r.subject ? `· ${r.subject}` : ''}
          </p>
          {r.channel === 'voice' ? (
            r.recording_id ? (
              <RecordingPlayer recordingId={r.recording_id} title={`Call with ${r.customer ?? r.customer_number ?? 'a customer'}`} />
            ) : (
              <p className="text-sm text-ink-2">This call has no recording (the queue does not record, or it was deleted).</p>
            )
          ) : (
            <div className="max-h-[70vh] overflow-auto">
              <TranscriptBody row={row} />
            </div>
          )}
          {r.channel === 'voice' ? (
            <div className="mt-4" aria-label="Notes on the recording">
              <p className="mb-1 text-sm font-medium">Notes on the recording</p>
              {marks.length === 0 ? <p className="text-[12px] text-ink-3">None.</p> : null}
              <ul className="mb-2 flex flex-col gap-1">
                {marks
                  .slice()
                  .sort((a, b) => a.at - b.at)
                  .map((m, n) => (
                    <li key={n} className="flex gap-2 text-sm">
                      <span className="tabular text-ink-3">{duration(m.at)}</span>
                      <span className="flex-1">{m.text}</span>
                      {editing ? (
                        <button type="button" className="text-[12px] text-ink-3 hover:text-break" onClick={() => setMarks(marks.filter((x) => x !== m))} aria-label={`Remove the note at ${duration(m.at)}`}>
                          ×
                        </button>
                      ) : null}
                    </li>
                  ))}
              </ul>
              {editing ? (
                <div className="flex gap-2">
                  <input value={markAt} onChange={(e) => setMarkAt(e.target.value)} placeholder="2:14" aria-label="At (m:ss)" className={`${inputClass} w-20`} />
                  <input value={markText} onChange={(e) => setMarkText(e.target.value)} placeholder="What happened here" aria-label="Note" className={`${inputClass} flex-1`} />
                  <Button
                    tone="quiet"
                    className="px-3 py-1.5 text-sm"
                    disabled={parseAt(markAt) === null || !markText.trim()}
                    onClick={() => {
                      setMarks([...marks, { at: parseAt(markAt)!, text: markText.trim() }]);
                      setMarkAt('');
                      setMarkText('');
                    }}
                  >
                    Add note
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}
        </Panel>

        <Panel className="p-4" aria-label="Scorecard">
          <div className="mb-3 flex items-center gap-2">
            <p className="font-medium">
              {r.scorecard_name} <span className="text-[12px] text-ink-3">v{r.version}</span>
            </p>
            <span className="ml-auto" aria-label="Score">
              <Result score={editing ? live!.score : r.score} passed={editing ? live!.passed : r.passed} critical={editing ? live!.critical : r.critical_failed} />
            </span>
          </div>
          <p className="mb-3 text-[12px] text-ink-3">
            Pass mark {r.pass_mark}%. A failed critical question (marked !) fails the review whatever the score. Reviewed by {r.reviewer_name ?? 'someone'}.
          </p>
          {r.definition.sections.map((s) => (
            <fieldset key={s.id} className="mb-4">
              <legend className="mb-2 text-sm font-semibold">{s.title}</legend>
              {s.questions.map((q) => {
                const v = answers[q.id]?.value ?? null;
                const choice = (val: Value, label: string) => (
                  <button
                    key={String(val)}
                    type="button"
                    role="radio"
                    aria-checked={v === val}
                    disabled={!editing}
                    onClick={() => set(q.id, val)}
                    className={`min-w-9 rounded-md border px-2 py-1 text-xs ${v === val ? 'border-ion bg-ion/10 font-semibold text-ion' : 'border-line'} ${editing ? 'hover:border-line-strong' : 'cursor-default'}`}
                  >
                    {label}
                  </button>
                );
                return (
                  <div key={q.id} className="mb-3 rounded-lg border border-line p-2.5">
                    <p className="text-sm">
                      {q.critical ? <span className="mr-1 font-bold text-break" title="Critical">!</span> : null}
                      {q.text} <span className="text-[11px] text-ink-3">({q.points} pts)</span>
                    </p>
                    {q.help ? <p className="text-[11px] text-ink-3">{q.help}</p> : null}
                    <div className="mt-1.5 flex flex-wrap gap-1" role="radiogroup" aria-label={q.text}>
                      {q.type === 'yes_no' ? [choice('yes', 'Yes'), choice('no', 'No')] : [1, 2, 3, 4, 5].map((n) => choice(n, String(n)))}
                      {q.allowNa ? choice('na', 'N/A') : null}
                    </div>
                    {editing ? (
                      <input value={answers[q.id]?.comment ?? ''} onChange={(e) => note(q.id, e.target.value)} placeholder="Comment (optional)" aria-label={`Comment on ${q.text}`} className={`${inputClass} mt-1.5 py-1 text-xs`} />
                    ) : answers[q.id]?.comment ? (
                      <p className="mt-1 text-[12px] text-ink-2">“{answers[q.id]?.comment}”</p>
                    ) : null}
                  </div>
                );
              })}
            </fieldset>
          ))}
          {editing ? (
            <Field label="Overall comment">{(fid) => <textarea id={fid} rows={3} className={inputClass} value={comment} onChange={(e) => setComment(e.target.value)} />}</Field>
          ) : r.comment ? (
            <p className="mb-3 rounded-lg bg-abyss/50 p-2 text-sm">{r.comment}</p>
          ) : null}

          {r.dispute_reason ? (
            <div className="mb-3 rounded-lg border border-break/40 p-2 text-sm">
              <p className="text-[12px] font-medium text-break">The agent disputed it</p>
              <p>{r.dispute_reason}</p>
              {r.dispute_reply ? (
                <>
                  <p className="mt-2 text-[12px] font-medium text-ink-2">Answer</p>
                  <p>{r.dispute_reply}</p>
                </>
              ) : null}
            </div>
          ) : null}

          {/* What can be done now */}
          {editing && r.status === 'draft' ? (
            <div className="flex flex-wrap gap-2">
              <Button tone="primary" className="px-4 py-2" disabled={busy || (live?.open ?? 1) > 0} onClick={() => void save().then(async (ok) => {
                  if (ok) await run('submit', undefined, 'Review submitted');
                })}>
                Submit review
              </Button>
              <Button tone="quiet" className="px-3 py-2" disabled={busy} onClick={() => void save().then((ok) => ok && useNova.getState().toast('Draft saved'))}>
                Save draft
              </Button>
              {live && live.open > 0 ? <span className="self-center text-[12px] text-ink-3">{live.open} questions to answer</span> : null}
            </div>
          ) : null}
          {editing && r.status === 'disputed' ? (
            <div aria-label="Answer the dispute">
              <Field label="Your answer to the agent" hint="Change any answers above first if you agree.">
                {(fid) => <textarea id={fid} rows={3} className={inputClass} value={reply} onChange={(e) => setReply(e.target.value)} />}
              </Field>
              <Button tone="primary" className="px-4 py-2" disabled={busy || reply.trim().length < 2} onClick={() => void run('reply', { reply, answers }, 'Answer sent')}>
                Send answer
              </Button>
            </div>
          ) : null}
          {agentView && r.status === 'submitted' ? (
            <div className="flex flex-col gap-2" aria-label="Your response">
              <Button tone="primary" className="px-4 py-2" disabled={busy} onClick={() => void run('acknowledge', undefined, 'Review acknowledged')}>
                Acknowledge
              </Button>
              <Field label="Or dispute it" hint="Say what you disagree with; the reviewer answers.">
                {(fid) => <textarea id={fid} rows={2} className={inputClass} value={reason} onChange={(e) => setReason(e.target.value)} />}
              </Field>
              <Button tone="quiet" className="px-4 py-2" disabled={busy || reason.trim().length < 5} onClick={() => void run('dispute', { reason }, 'Dispute sent')}>
                Dispute
              </Button>
            </div>
          ) : null}
        </Panel>
      </div>
    </div>
  );
}

/** Quality in the Reports hub: a list, and a review opened from it. */
export function QualityPage({ kind }: { kind: 'toreview' | 'reviews' | 'mine' }) {
  const [open, setOpen] = useState<string | null>(null);
  if (open) return <QaReviewView id={open} onBack={() => setOpen(null)} />;
  if (kind === 'toreview') return <QaToReview onOpen={setOpen} />;
  return <QaReviews onOpen={setOpen} mine={kind === 'mine'} />;
}
